import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Users, 
  GraduationCap, 
  Briefcase,
  Building,
  Clock,
  Database,
  Settings,
  Trash2,
  UserPlus,
  School,
  Contact
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useConfirm } from '../ConfirmProvider';

export default function WondeIntegration({ customer, onCustomerUpdate }) {
  const confirm = useConfirm();
  const [apiKey, setApiKey] = useState(customer?.wonde_api_key || '');
  const [isEnabled, setIsEnabled] = useState(customer?.wonde_enabled || false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(customer?.wonde_school_id || null);
  const [syncData, setSyncData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [activeDataTab, setActiveDataTab] = useState('students');
  const [createUsersDialog, setCreateUsersDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [creatingUsers, setCreatingUsers] = useState(false);
  const [userTypeToCreate, setUserTypeToCreate] = useState('students');

  useEffect(() => {
    if (customer?.wonde_api_key && customer?.wonde_enabled) {
      loadSyncData();
    }
  }, [customer]);

  const loadSyncData = async () => {
    try {
      setLoadingData(true);
      const data = await api.wondeGetSyncData();
      if (data.has_data) {
        setSyncData(data);
      }
    } catch (error) {
      console.error('Failed to load sync data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.updateCustomer(customer.id, {
        wonde_api_key: apiKey,
        wonde_enabled: isEnabled
      });
      onCustomerUpdate();
      toast.success('Wonde settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleTestConnection = async () => {
    if (!apiKey) {
      toast.error('Please enter an API key first');
      return;
    }

    // Save the API key first
    try {
      await api.updateCustomer(customer.id, {
        wonde_api_key: apiKey,
        wonde_enabled: true
      });
      setIsEnabled(true);
    } catch (error) {
      toast.error('Failed to save API key');
      return;
    }

    setTesting(true);
    setConnectionStatus(null);
    
    try {
      const result = await api.wondeTestConnection();
      setConnectionStatus(result);
      
      if (result.success) {
        setSchools(result.schools || []);
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: error.response?.data?.detail || 'Connection test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
      onCustomerUpdate();
    }
  };

  const handleSync = async () => {
    if (!selectedSchool) {
      toast.error('Please select a school first');
      return;
    }

    setSyncing(true);
    try {
      const result = await api.wondeSync(selectedSchool);
      toast.success(result.message);
      await loadSyncData();
      onCustomerUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteSyncData = async () => {
    if (!(await confirm({
      title: 'Delete all synced Wonde data?',
      description: 'This cannot be undone. You will need to run a fresh sync.',
      confirmText: 'Delete data',
    }))) {
      return;
    }

    try {
      await api.wondeDeleteSyncData();
      setSyncData(null);
      toast.success('Synced data deleted');
      onCustomerUpdate();
    } catch (error) {
      toast.error('Failed to delete data');
    }
  };

  const handleCreateUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user to create');
      return;
    }

    setCreatingUsers(true);
    try {
      const result = await api.wondeCreateUsers(userTypeToCreate, selectedUsers);
      
      if (result.created > 0) {
        toast.success(`Created ${result.created} user(s)`);
      }
      if (result.skipped > 0) {
        toast.info(`Skipped ${result.skipped} user(s) (already exist or no email)`);
      }
      if (result.errors?.length > 0) {
        console.log('Creation errors:', result.errors);
      }
      
      setCreateUsersDialog(false);
      setSelectedUsers([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create users');
    } finally {
      setCreatingUsers(false);
    }
  };

  const toggleUserSelection = (wondeId) => {
    setSelectedUsers(prev => 
      prev.includes(wondeId) 
        ? prev.filter(id => id !== wondeId)
        : [...prev, wondeId]
    );
  };

  const selectAllUsers = (users) => {
    const ids = users.filter(u => u.email).map(u => u.wonde_id);
    setSelectedUsers(ids);
  };

  const renderDataTable = (data, type) => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No {type} data available
        </div>
      );
    }

    if (type === 'students') {
      return (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {data.map((student, idx) => (
            <div key={student.wonde_id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <GraduationCap className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium">{student.full_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Year {student.year_group || 'N/A'} • {student.email || 'No email'}
                  </p>
                </div>
              </div>
              {student.contacts?.length > 0 && (
                <Badge variant="outline">
                  {student.contacts.length} contact(s)
                </Badge>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (type === 'employees') {
      return (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {data.map((emp, idx) => (
            <div key={emp.wonde_id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium">{emp.full_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {emp.role || 'Staff'} • {emp.email || 'No email'}
                  </p>
                </div>
              </div>
              {emp.is_teaching_staff && (
                <Badge variant="secondary">Teaching</Badge>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (type === 'contacts') {
      return (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {data.map((contact, idx) => (
            <div key={contact.wonde_id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Contact className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="font-medium">{contact.full_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {contact.email || 'No email'} • {contact.phone || 'No phone'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (type === 'classes') {
      return (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {data.map((cls, idx) => (
            <div key={cls.wonde_id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <School className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="font-medium">{cls.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {cls.subject || 'No subject'} • {cls.student_count} students
                  </p>
                </div>
              </div>
              {cls.code && <Badge variant="outline">{cls.code}</Badge>}
            </div>
          ))}
        </div>
      );
    }

    if (type === 'groups') {
      return (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {data.map((group, idx) => (
            <div key={group.wonde_id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-teal-500" />
                <div>
                  <p className="font-medium">{group.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {group.type || 'Group'} • {group.student_count} members
                  </p>
                </div>
              </div>
              {group.code && <Badge variant="outline">{group.code}</Badge>}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Connection Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Wonde Integration
          </CardTitle>
          <CardDescription>
            Connect to Wonde to sync student, staff, and parent data from your school&apos;s MIS
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Enable Wonde Integration</Label>
              <p className="text-sm text-muted-foreground">
                Allow syncing data from Wonde
              </p>
            </div>
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => {
                setIsEnabled(checked);
                if (!checked) {
                  setConnectionStatus(null);
                  setSchools([]);
                }
              }}
            />
          </div>

          {isEnabled && (
            <>
              <div className="space-y-2">
                <Label htmlFor="wonde-api-key">Wonde API Token</Label>
                <Input
                  id="wonde-api-key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your Wonde API token"
                />
                <p className="text-xs text-muted-foreground">
                  Get your API token from{' '}
                  <a 
                    href="https://apps.wonde.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    apps.wonde.com
                  </a>
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleTestConnection} 
                  disabled={testing || !apiKey}
                  variant="outline"
                >
                  {testing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Test Connection
                </Button>
                <Button onClick={handleSaveSettings}>
                  Save Settings
                </Button>
              </div>

              {connectionStatus && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                  connectionStatus.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {connectionStatus.success ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  {connectionStatus.message}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* School Selection & Sync Card */}
      {isEnabled && schools.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Select School & Sync
            </CardTitle>
            <CardDescription>
              Choose a school to sync data from
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Approved Schools</Label>
              <div className="space-y-2">
                {schools.map((school) => (
                  <div 
                    key={school.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedSchool === school.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedSchool(school.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{school.name}</p>
                        <p className="text-sm text-muted-foreground">
                          URN: {school.urn} • LA: {school.la_code}
                        </p>
                      </div>
                      {selectedSchool === school.id && (
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button 
                onClick={handleSync} 
                disabled={syncing || !selectedSchool}
              >
                {syncing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Sync Now
              </Button>
              
              {customer?.wonde_last_sync && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Last sync: {formatDistanceToNow(new Date(customer.wonde_last_sync), { addSuffix: true })}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Synced Data Card */}
      {syncData?.has_data && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Synced Data from {syncData.school_name}
                </CardTitle>
                <CardDescription>
                  Last synced: {formatDistanceToNow(new Date(syncData.synced_at), { addSuffix: true })}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Dialog open={createUsersDialog} onOpenChange={setCreateUsersDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Users
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create Taptile Users from Wonde Data</DialogTitle>
                      <DialogDescription>
                        Select users to create Taptile accounts for. Only users with email addresses can be created.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs value={userTypeToCreate} onValueChange={(v) => { setUserTypeToCreate(v); setSelectedUsers([]); }}>
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="students">
                          <GraduationCap className="w-4 h-4 mr-2" />
                          Students ({syncData.counts?.students || 0})
                        </TabsTrigger>
                        <TabsTrigger value="employees">
                          <Briefcase className="w-4 h-4 mr-2" />
                          Staff ({syncData.counts?.employees || 0})
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="students" className="space-y-3">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-muted-foreground">
                            {selectedUsers.length} selected
                          </p>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => selectAllUsers(syncData.data?.students || [])}
                          >
                            Select All with Email
                          </Button>
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {syncData.data?.students?.map((student) => (
                            <div 
                              key={student.wonde_id}
                              className={`flex items-center gap-3 p-2 rounded-lg ${
                                !student.email ? 'opacity-50' : 'hover:bg-muted/50'
                              }`}
                            >
                              <Checkbox
                                checked={selectedUsers.includes(student.wonde_id)}
                                onCheckedChange={() => toggleUserSelection(student.wonde_id)}
                                disabled={!student.email}
                              />
                              <div className="flex-1">
                                <p className="font-medium">{student.full_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {student.email || 'No email - cannot create'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="employees" className="space-y-3">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-muted-foreground">
                            {selectedUsers.length} selected
                          </p>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => selectAllUsers(syncData.data?.employees || [])}
                          >
                            Select All with Email
                          </Button>
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {syncData.data?.employees?.map((emp) => (
                            <div 
                              key={emp.wonde_id}
                              className={`flex items-center gap-3 p-2 rounded-lg ${
                                !emp.email ? 'opacity-50' : 'hover:bg-muted/50'
                              }`}
                            >
                              <Checkbox
                                checked={selectedUsers.includes(emp.wonde_id)}
                                onCheckedChange={() => toggleUserSelection(emp.wonde_id)}
                                disabled={!emp.email}
                              />
                              <div className="flex-1">
                                <p className="font-medium">{emp.full_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {emp.email || 'No email - cannot create'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    </Tabs>
                    
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setCreateUsersDialog(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateUsers}
                        disabled={creatingUsers || selectedUsers.length === 0}
                      >
                        {creatingUsers ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4 mr-2" />
                        )}
                        Create {selectedUsers.length} User(s)
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDeleteSyncData}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Data
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge variant="secondary" className="flex items-center gap-1">
                <GraduationCap className="w-3 h-3" />
                {syncData.counts?.students || 0} Students
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-1">
                <Briefcase className="w-3 h-3" />
                {syncData.counts?.employees || 0} Staff
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-1">
                <Contact className="w-3 h-3" />
                {syncData.counts?.contacts || 0} Contacts
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-1">
                <School className="w-3 h-3" />
                {syncData.counts?.classes || 0} Classes
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {syncData.counts?.groups || 0} Groups
              </Badge>
            </div>
            
            {/* Data Tabs */}
            <Tabs value={activeDataTab} onValueChange={setActiveDataTab}>
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="students">Students</TabsTrigger>
                <TabsTrigger value="employees">Staff</TabsTrigger>
                <TabsTrigger value="contacts">Contacts</TabsTrigger>
                <TabsTrigger value="classes">Classes</TabsTrigger>
                <TabsTrigger value="groups">Groups</TabsTrigger>
              </TabsList>
              
              <TabsContent value="students">
                {renderDataTable(syncData.data?.students, 'students')}
              </TabsContent>
              <TabsContent value="employees">
                {renderDataTable(syncData.data?.employees, 'employees')}
              </TabsContent>
              <TabsContent value="contacts">
                {renderDataTable(syncData.data?.contacts, 'contacts')}
              </TabsContent>
              <TabsContent value="classes">
                {renderDataTable(syncData.data?.classes, 'classes')}
              </TabsContent>
              <TabsContent value="groups">
                {renderDataTable(syncData.data?.groups, 'groups')}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Help Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Setup Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="space-y-2">
            <p className="font-medium">How to get started with Wonde:</p>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Create an account at <a href="https://apps.wonde.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">apps.wonde.com</a></li>
              <li>Register your application and get an API token</li>
              <li>Request access to your school(s)</li>
              <li>Once approved, enter your API token above</li>
              <li>Test the connection and sync your data</li>
            </ol>
          </div>
          <div className="pt-2 border-t">
            <p className="text-muted-foreground">
              Wonde connects to your school&apos;s Management Information System (MIS) like SIMS, Arbor, or Bromcom 
              to sync student, staff, and parent data automatically.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
